import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, SectionTitle, StatCard } from "@/components/croc/ui";
import { fmtUsd, shortAddr } from "@/lib/format";
import api from "@/lib/api";
import { Copy, Users, Gift, Link2, Share2, UserCheck, Network } from "lucide-react";
import { RankShareCard } from "@/components/croc/RankShareCard";
import { MarketingPoints } from "@/components/croc/MarketingPoints";
import { MarketingFeed } from "@/components/croc/MarketingFeed";

export default function Referral() {
  const { address, user } = useWallet();
  const { data } = usePolling(() => api.referrals(address), [address], 30000);
  const { data: directs } = usePolling(() => api.chainDirects(address || "0x0"), [address], 30000);
  const code = user?.referral_code || "";
  const link = `${window.location.origin}/?ref=${code}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div>
      <SectionTitle
        eyebrow="Direct Sponsor (5%) & Referral Hub"
        title="Referral Hub"
        subtitle="Earn 5% of every direct referral's stake — paid from the revenue pool, never from principal."
        testid="referral-title"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Direct Referrals" value={data?.direct_count ?? 0} icon={UserCheck} accent="emerald" testid="stat-directs" />
        <StatCard label="Direct Bonus" value={fmtUsd(user?.direct_bonus_earned)} icon={Gift} accent="gold" testid="stat-direct-bonus" />
        <StatCard label="Override Income" value={fmtUsd(user?.level_income_earned)} icon={Users} accent="emerald" testid="stat-level-income" />
        <StatCard label="Team Volume" value={fmtUsd(user?.team_volume)} icon={Share2} accent="gold" testid="stat-team-volume" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2"><MarketingPoints /></div>
        <div className="lg:col-span-1"><MarketingFeed /></div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1"><RankShareCard /></div>
        <Panel className="lg:col-span-2" data-testid="referral-directs">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><UserCheck className="w-4 h-4 emerald-text" /><span className="font-display font-bold text-white">Your Directs · On-chain contribution</span></div>
            <span className="text-xs text-muted-foreground">Total {fmtUsd(directs?.total_contribution ?? 0)}</span>
          </div>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
            {(directs?.directs ?? []).map((d, i) => (
              <div key={d.address} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-2.5" data-testid={`direct-row-${i}`}>
                <span className="font-num text-sm text-white">{shortAddr(d.address)}</span>
                <span className="font-num text-sm emerald-text">{fmtUsd(d.team_volume)}</span>
              </div>
            ))}
            {(directs?.directs?.length ?? 0) === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No on-chain directs yet — share your link to grow your team.</div>}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel glow="gold" data-testid="referral-link-panel">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 gold-text" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Your Referral Link</span>
          </div>
          <div className="bg-white p-3 rounded-xl w-fit mx-auto mb-4">
            <QRCodeSVG value={link} size={148} bgColor="#ffffff" fgColor="#0F1712" level="M" />
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-3 mb-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Referral Code</div>
            <div className="flex items-center justify-between">
              <span className="font-num font-bold gold-text">{code}</span>
              <button data-testid="referral-code-copy-button" onClick={() => copy(code, "Code")} className="text-muted-foreground hover:text-white">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
          <button
            data-testid="referral-link-copy-button"
            onClick={() => copy(link, "Referral link")}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#1CFD4C] py-3 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors"
          >
            <Copy className="w-4 h-4" /> Copy Referral Link
          </button>
        </Panel>

        <Panel className="lg:col-span-2" data-testid="referral-team">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 emerald-text" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Your Direct Team</span>
          </div>
          {(data?.directs?.length ?? 0) === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No referrals yet. Share your link to start earning the 5% direct bonus.
            </div>
          ) : (
            <div className="space-y-2">
              {data.directs.map((d, i) => (
                <div key={d.address} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3 gap-3" data-testid={`direct-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[#10B981]/15 grid place-items-center text-xs font-num emerald-text shrink-0">{i + 1}</div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-num text-xs text-white break-all" data-testid={`direct-address-${i}`}>{d.address}</span>
                        <button
                          data-testid={`direct-address-copy-${i}`}
                          onClick={() => copy(d.address, "Wallet address")}
                          className="text-muted-foreground hover:text-white shrink-0"
                          title="Copy wallet address"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-[10px] text-muted-foreground">Direct member</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-num text-white">{fmtUsd(d.staked)}</div>
                    <div className="text-[10px] text-muted-foreground">staked</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <TeamLevels address={address} copy={copy} />
    </div>
  );
}

function TeamLevels({ address, copy }) {
  const { data } = usePolling(() => api.teamLevels(address || "0x0"), [address], 30000);
  const [level, setLevel] = useState(1);
  const levels = data?.levels ?? [];
  const active = levels.find((l) => l.level === level) || levels[0];

  return (
    <Panel className="mt-6" data-testid="team-levels-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 emerald-text" />
          <span className="font-display font-bold text-white">Team Levels</span>
          <span className="text-xs text-muted-foreground">level-wise view of your whole team (1–21)</span>
        </div>
        <span className="text-xs text-muted-foreground" data-testid="team-levels-total">
          {data?.total_members ?? 0} member(s) in team
        </span>
      </div>

      {levels.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground text-sm" data-testid="team-levels-empty">
          No team yet — share your referral link to build your first level.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="team-level-tabs">
            {levels.map((l) => (
              <button key={l.level} data-testid={`team-level-tab-${l.level}`} onClick={() => setLevel(l.level)}
                className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                  active?.level === l.level
                    ? "bg-[#1CFD4C]/15 text-[#1CFD4C] border-[#1CFD4C]/40"
                    : "bg-[#090E0B] text-muted-foreground border-white/10 hover:text-white"
                }`}>
                L{l.level} · {l.count}
              </button>
            ))}
          </div>

          {active && (
            <>
              <div className="flex flex-wrap gap-4 mb-3 text-[11px] text-muted-foreground" data-testid="team-level-summary">
                <span>Level {active.level} total own top-up: <span className="font-num text-white">{fmtUsd(active.total_own_topup)}</span></span>
                <span>direct business: <span className="font-num text-white">{fmtUsd(active.total_direct_business)}</span></span>
                <span>team business: <span className="font-num text-white">{fmtUsd(active.total_team_business)}</span></span>
              </div>
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[#0B120E]">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/10">
                      <th className="py-2 pr-2">#</th>
                      <th className="py-2 pr-2">Wallet</th>
                      <th className="py-2 pr-2">Sponsor ID</th>
                      <th className="py-2 pr-2 text-right">Own Top-up</th>
                      <th className="py-2 pr-2 text-right">Direct Business</th>
                      <th className="py-2 text-right">Team Business</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.members.map((m, i) => (
                      <tr key={m.address} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`team-row-${i}`}>
                        <td className="py-2.5 pr-2 text-muted-foreground font-num">{i + 1}</td>
                        <td className="py-2.5 pr-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-num text-white break-all">{m.address}</span>
                            <button data-testid={`team-row-copy-${i}`} onClick={() => copy(m.address, "Wallet address")}
                              className="text-muted-foreground hover:text-white shrink-0" title="Copy wallet">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                          {m.referral_code && <div className="text-[10px] emerald-text font-num">ID: {m.referral_code}</div>}
                        </td>
                        <td className="py-2.5 pr-2 font-num gold-text">{m.sponsor_code || "—"}</td>
                        <td className="py-2.5 pr-2 text-right font-num text-white">{fmtUsd(m.own_topup)}</td>
                        <td className="py-2.5 pr-2 text-right font-num emerald-text">{fmtUsd(m.direct_business)}</td>
                        <td className="py-2.5 text-right font-num gold-text">{fmtUsd(m.team_business)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </Panel>
  );
}
